import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Sparkles, ImageIcon, Wand2, Upload, Copy, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useNotifications } from '../../hooks/useNotifications';
import I18nTripleFields, { I18nTriple } from '../../components/Admin/I18nTripleFields';
import { emptyI18nTriple } from '../../i18n/localizeContent';
import IconPicker from '../../components/Admin/IconPicker';
import { resolveIcon } from '../../utils/iconCatalog';

interface PixabayOption {
  pixabayId: number;
  previewURL: string;
  largeImageURL: string;
  tags: string;
}

interface AvatarOption {
  _id: string;
  name: string;
  slug: string;
}

interface TeamMemberOption {
  _id: string;
  nome: string;
  cargo?: string;
}

interface Benefit {
  _id?: string;
  icon: string;
  i18n: { pt: { text: string }; en: { text: string }; es: { text: string } };
}

interface SocialProofItem {
  _id?: string;
  value: string;
  i18n: { pt: { label: string }; en: { label: string }; es: { label: string } };
}

interface LandingPageFormData {
  internalName: string;
  slug: string;
  avatar: string;
  teamMember: string;
  status: 'draft' | 'published';
  hero: {
    mediaType: 'image' | 'video';
    image: { source: 'upload' | 'pixabay' | 'ai' | 'link'; url: string };
    video: { source: 'upload' | 'link'; url: string };
  };
}

const emptyBenefit = (): Benefit => ({ icon: '', i18n: { pt: { text: '' }, en: { text: '' }, es: { text: '' } } });
const emptySocialProofItem = (): SocialProofItem => ({ value: '', i18n: { pt: { label: '' }, en: { label: '' }, es: { label: '' } } });

const LandingPageForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEditing = Boolean(id);
  const notifications = useNotifications();
  const heroVideoInputRef = useRef<HTMLInputElement>(null);

  const [i18n, setI18n] = useState<I18nTriple>(emptyI18nTriple());
  const [subject, setSubject] = useState('');
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [socialProof, setSocialProof] = useState<SocialProofItem[]>([]);
  const [avatars, setAvatars] = useState<AvatarOption[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMemberOption[]>([]);
  const [formData, setFormData] = useState<LandingPageFormData>({
    internalName: '',
    slug: '',
    avatar: '',
    teamMember: '',
    status: 'draft',
    hero: { mediaType: 'image', image: { source: 'upload', url: '' }, video: { source: 'link', url: '' } }
  });

  const [loading, setLoading] = useState(false);
  const [generatingText, setGeneratingText] = useState(false);
  const [searchingImages, setSearchingImages] = useState(false);
  const [generatingAiImage, setGeneratingAiImage] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [pixabayOptions, setPixabayOptions] = useState<PixabayOption[]>([]);
  const [aiPromptInput, setAiPromptInput] = useState('');
  const [showAiImagePrompt, setShowAiImagePrompt] = useState(false);
  const [openIconPickerIndex, setOpenIconPickerIndex] = useState<number | null>(null);

  useEffect(() => {
    axios.get('/avatars?limit=100').then((res) => setAvatars(res.data.avatars)).catch(() => setAvatars([]));
  }, []);

  useEffect(() => {
    axios.get('/equipe/admin').then((res) => setTeamMembers(res.data)).catch(() => setTeamMembers([]));
  }, []);

  const fetchLandingPage = useCallback(async () => {
    if (!id) return;
    try {
      const response = await axios.get(`/landing-pages/${id}`);
      const lp = response.data;
      setFormData({
        internalName: lp.internalName || '',
        slug: lp.slug || '',
        avatar: lp.avatar?._id || lp.avatar || '',
        teamMember: lp.teamMember?._id || lp.teamMember || '',
        status: lp.status || 'draft',
        hero: lp.hero || { mediaType: 'image', image: { source: 'upload', url: '' }, video: { source: 'link', url: '' } }
      });
      setBenefits(lp.benefits || []);
      setSocialProof(lp.socialProof || []);
      setI18n({
        pt: { ...lp.i18n?.pt },
        en: { ...lp.i18n?.en },
        es: { ...lp.i18n?.es }
      });
    } catch (error) {
      console.error('Erro ao carregar landing page:', error);
    }
  }, [id]);

  useEffect(() => {
    if (isEditing) fetchLandingPage();
  }, [isEditing, fetchLandingPage]);

  const handleGenerateContent = async () => {
    if (!subject.trim()) {
      notifications.showWarning('Validação', 'Informe o assunto/oferta antes de gerar o conteúdo.');
      return;
    }
    try {
      setGeneratingText(true);
      const response = await axios.post('/landing-pages/generate-content', { subject });
      const { headline, subheadline, ctaLabel, benefits: genBenefits, metaTitle, metaDescription, imageQuery } = response.data;
      setI18n((prev) => ({
        pt: { ...prev.pt, headline: headline?.pt, subheadline: subheadline?.pt, ctaLabel: ctaLabel?.pt, metaTitle: metaTitle?.pt, metaDescription: metaDescription?.pt },
        en: { ...prev.en, headline: headline?.en, subheadline: subheadline?.en, ctaLabel: ctaLabel?.en, metaTitle: metaTitle?.en, metaDescription: metaDescription?.en },
        es: { ...prev.es, headline: headline?.es, subheadline: subheadline?.es, ctaLabel: ctaLabel?.es, metaTitle: metaTitle?.es, metaDescription: metaDescription?.es }
      }));
      if (Array.isArray(genBenefits) && genBenefits.length > 0) {
        setBenefits(genBenefits.map((b: any) => ({
          icon: '',
          i18n: { pt: { text: b.pt || '' }, en: { text: b.en || '' }, es: { text: b.es || '' } }
        })));
      }
      setPixabayOptions([]);
      setAiPromptInput(imageQuery || subject);
      if (!formData.internalName) {
        setFormData((prev) => ({ ...prev, internalName: subject }));
      }
    } catch (error: any) {
      console.error('Erro ao gerar conteúdo:', error);
      notifications.showError('Erro ao gerar conteúdo', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setGeneratingText(false);
    }
  };

  const handleSearchPixabay = async () => {
    const query = aiPromptInput || subject;
    if (!query.trim()) {
      notifications.showWarning('Validação', 'Informe o assunto ou gere o conteúdo antes de buscar imagens.');
      return;
    }
    try {
      setSearchingImages(true);
      const response = await axios.post('/landing-pages/fetch-image-options', { subject, query });
      setPixabayOptions(response.data.options || []);
    } catch (error: any) {
      notifications.showError('Erro ao buscar imagens', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setSearchingImages(false);
    }
  };

  const handleSelectPixabayImage = async (option: PixabayOption) => {
    try {
      setSearchingImages(true);
      const response = await axios.post('/landing-pages/select-image', { pixabayId: option.pixabayId, imageURL: option.largeImageURL });
      setFormData((prev) => ({ ...prev, hero: { ...prev.hero, mediaType: 'image', image: { source: 'pixabay', url: response.data.url } } }));
      setPixabayOptions([]);
    } catch (error: any) {
      notifications.showError('Erro ao salvar imagem', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setSearchingImages(false);
    }
  };

  const handleGenerateAiImage = async () => {
    if (!aiPromptInput.trim()) {
      notifications.showWarning('Validação', 'Descreva a imagem que deseja gerar.');
      return;
    }
    try {
      setGeneratingAiImage(true);
      const response = await axios.post('/landing-pages/generate-ai-image', { prompt: aiPromptInput });
      setFormData((prev) => ({ ...prev, hero: { ...prev.hero, mediaType: 'image', image: { source: 'ai', url: response.data.url } } }));
      setShowAiImagePrompt(false);
    } catch (error: any) {
      notifications.showError('Erro ao gerar imagem', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setGeneratingAiImage(false);
    }
  };

  const handleHeroVideoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingVideo(true);
      const data = new FormData();
      data.append('video', file);
      const response = await axios.post('/landing-pages/upload-hero-video', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormData((prev) => ({ ...prev, hero: { ...prev.hero, mediaType: 'video', video: { source: 'upload', url: response.data.url } } }));
    } catch (error: any) {
      notifications.showError('Erro ao enviar vídeo', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setUploadingVideo(false);
    }
  };

  const addBenefit = () => setBenefits((prev) => [...prev, emptyBenefit()]);
  const removeBenefit = (index: number) => setBenefits((prev) => prev.filter((_, i) => i !== index));
  const patchBenefit = (index: number, lang: 'pt' | 'en' | 'es', text: string) =>
    setBenefits((prev) => prev.map((b, i) => (i === index ? { ...b, i18n: { ...b.i18n, [lang]: { text } } } : b)));
  const patchBenefitIcon = (index: number, iconName: string) => {
    setBenefits((prev) => prev.map((b, i) => (i === index ? { ...b, icon: iconName } : b)));
    setOpenIconPickerIndex(null);
  };

  const addSocialProofItem = () => setSocialProof((prev) => [...prev, emptySocialProofItem()]);
  const removeSocialProofItem = (index: number) => setSocialProof((prev) => prev.filter((_, i) => i !== index));
  const patchSocialProofValue = (index: number, value: string) =>
    setSocialProof((prev) => prev.map((item, i) => (i === index ? { ...item, value } : item)));
  const patchSocialProofLabel = (index: number, lang: 'pt' | 'en' | 'es', label: string) =>
    setSocialProof((prev) => prev.map((item, i) => (i === index ? { ...item, i18n: { ...item.i18n, [lang]: { label } } } : item)));

  const handleSubmit = async (e: React.FormEvent, publishNow?: boolean) => {
    e.preventDefault();
    setLoading(true);

    const status = publishNow ? 'published' : formData.status;

    if (formData.internalName.trim().length < 3) {
      notifications.showWarning('Validação', 'Informe um nome interno para a campanha.');
      setLoading(false);
      return;
    }
    if (!formData.avatar) {
      notifications.showWarning('Validação', 'Selecione o avatar para onde o CTA vai levar.');
      setLoading(false);
      return;
    }
    if (status === 'published' && !i18n.pt?.headline?.trim()) {
      notifications.showWarning('Validação', 'A headline (pt) é obrigatória para publicar.');
      setLoading(false);
      return;
    }

    const payload = { ...formData, status, slug: formData.slug, i18n, benefits, socialProof };

    try {
      if (isEditing) {
        await axios.put(`/landing-pages/${id}`, payload);
        notifications.showSuccess('Landing page atualizada!', 'As alterações foram salvas.');
      } else {
        await axios.post('/landing-pages', payload);
        notifications.showSuccess('Landing page criada!', 'A página foi criada com sucesso.');
      }
      setTimeout(() => navigate('/admin/landing-pages'), 1000);
    } catch (error: any) {
      console.error('Erro ao salvar landing page:', error);
      notifications.showError('Erro ao salvar', error.response?.data?.message || 'Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const publicUrl = formData.slug ? `${window.location.origin}/l/${formData.slug}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-center space-x-4">
          <button onClick={() => navigate('/admin/landing-pages')} className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{isEditing ? t('admin.landingPageEdit') : t('admin.landingPageNew')}</h1>
            <p className="text-gray-600">Página de funil de vendas para tráfego pago (Facebook/Instagram Ads).</p>
          </div>
        </div>

        {publicUrl && (
          <div className="card mb-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-gray-700 truncate">{publicUrl}</div>
            <button
              type="button"
              onClick={() => { navigator.clipboard.writeText(publicUrl); notifications.showSuccess('Copiado!', 'Link copiado para a área de transferência.'); }}
              className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5"
            >
              <Copy className="w-4 h-4" /> Copiar link
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Assunto / oferta da campanha</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <input
                      type="text"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      placeholder="Ex: Visto de trabalho para Portugal, processo rápido"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={handleGenerateContent}
                      disabled={generatingText}
                      className="btn-primary flex items-center justify-center space-x-2 whitespace-nowrap disabled:opacity-50"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span>{generatingText ? 'Gerando...' : 'Gerar conteúdo com IA'}</span>
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome interno (campanha)</label>
                    <input
                      type="text"
                      value={formData.internalName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, internalName: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Slug (URL curta do anúncio)</label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => setFormData((prev) => ({ ...prev, slug: e.target.value }))}
                      placeholder="visto-trabalho-natal"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <I18nTripleFields mode="landingPage" value={i18n} onChange={setI18n} />
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Benefícios (bullets)</h3>
                  <button type="button" onClick={addBenefit} className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5">
                    <Plus className="w-4 h-4" /> Adicionar
                  </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">O ícone escolhido aparece em destaque, num círculo, na página pública.</p>
                <div className="space-y-3">
                  {benefits.map((benefit, index) => {
                    const BenefitIcon = resolveIcon(benefit.icon);
                    return (
                      <div key={benefit._id || index}>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setOpenIconPickerIndex((prev) => (prev === index ? null : index))}
                            title="Escolher ícone"
                            className={`shrink-0 w-10 h-10 rounded-lg border flex items-center justify-center transition-colors ${
                              openIconPickerIndex === index
                                ? 'border-primary-500 bg-primary-50 text-primary-600'
                                : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            <BenefitIcon className="w-5 h-5" />
                          </button>
                          <input
                            type="text"
                            value={benefit.i18n.pt?.text || ''}
                            onChange={(e) => patchBenefit(index, 'pt', e.target.value)}
                            placeholder="Benefício (pt) — traduções en/es via IA acima"
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                          />
                          <button type="button" onClick={() => removeBenefit(index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {openIconPickerIndex === index && (
                          <div className="mt-2 ml-12">
                            <IconPicker value={benefit.icon} onChange={(iconName) => patchBenefitIcon(index, iconName)} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {benefits.length === 0 && <p className="text-sm text-gray-500">Nenhum benefício adicionado.</p>}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Prova social (números de confiança)</h3>
                    <p className="text-xs text-gray-500 mt-1">Ex.: valor "+500" com legenda "processos concluídos". Aparece antes do CTA.</p>
                  </div>
                  <button type="button" onClick={addSocialProofItem} className="btn-outline inline-flex items-center gap-1.5 text-sm px-3 py-1.5">
                    <Plus className="w-4 h-4" /> Adicionar
                  </button>
                </div>
                <div className="space-y-3">
                  {socialProof.map((item, index) => (
                    <div key={item._id || index} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={item.value}
                        onChange={(e) => patchSocialProofValue(index, e.target.value)}
                        placeholder="+500"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="text"
                        value={item.i18n.pt?.label || ''}
                        onChange={(e) => patchSocialProofLabel(index, 'pt', e.target.value)}
                        placeholder="Legenda (pt) — ex.: processos concluídos"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
                      />
                      <button type="button" onClick={() => removeSocialProofItem(index)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {socialProof.length === 0 && <p className="text-sm text-gray-500">Nenhum número de confiança adicionado.</p>}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Mídia principal (hero)</h3>
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, mediaType: 'image' } }))}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${formData.hero.mediaType === 'image' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Imagem
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, mediaType: 'video' } }))}
                    className={`px-3 py-1.5 rounded-md text-sm font-medium ${formData.hero.mediaType === 'video' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                  >
                    Vídeo
                  </button>
                </div>

                {formData.hero.mediaType === 'image' ? (
                  <>
                    {formData.hero.image.url && (
                      <div className="mb-4">
                        <img src={formData.hero.image.url} alt="Imagem selecionada" className="h-40 w-full object-cover rounded-lg" />
                        <p className="text-xs text-gray-500 mt-1">Fonte: {formData.hero.image.source}</p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 mb-4">
                      <button type="button" onClick={handleSearchPixabay} disabled={searchingImages} className="btn-secondary flex items-center space-x-2 disabled:opacity-50">
                        <ImageIcon className="w-4 h-4" />
                        <span>{searchingImages ? 'Buscando...' : 'Buscar imagens (Pixabay)'}</span>
                      </button>
                      <button type="button" onClick={() => setShowAiImagePrompt((prev) => !prev)} className="btn-secondary flex items-center space-x-2">
                        <Wand2 className="w-4 h-4" />
                        <span>Gerar imagem com IA</span>
                      </button>
                    </div>
                    {showAiImagePrompt && (
                      <div className="flex items-center space-x-2 mb-4">
                        <input
                          type="text"
                          value={aiPromptInput}
                          onChange={(e) => setAiPromptInput(e.target.value)}
                          placeholder="Descreva a imagem que deseja gerar..."
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                        <button type="button" onClick={handleGenerateAiImage} disabled={generatingAiImage} className="btn-primary whitespace-nowrap disabled:opacity-50">
                          {generatingAiImage ? 'Gerando...' : 'Gerar'}
                        </button>
                      </div>
                    )}
                    {pixabayOptions.length > 0 && (
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                        {pixabayOptions.map((option) => (
                          <button
                            key={option.pixabayId}
                            type="button"
                            onClick={() => handleSelectPixabayImage(option)}
                            className="rounded-lg overflow-hidden border border-gray-200 hover:ring-2 hover:ring-primary-500 transition-shadow"
                            title={option.tags}
                          >
                            <img src={option.previewURL} alt={option.tags} className="h-20 w-full object-cover" />
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex gap-2 mb-4">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, video: { ...prev.hero.video, source: 'upload' } } }))}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${formData.hero.video.source === 'upload' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                      >
                        Upload
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, video: { ...prev.hero.video, source: 'link' } } }))}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium ${formData.hero.video.source === 'link' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'}`}
                      >
                        Link
                      </button>
                    </div>
                    {formData.hero.video.source === 'upload' ? (
                      <div>
                        {formData.hero.video.url && <p className="text-xs text-gray-500 mb-2 break-all">{formData.hero.video.url}</p>}
                        <input ref={heroVideoInputRef} type="file" accept="video/*" onChange={handleHeroVideoUpload} disabled={uploadingVideo} />
                        {uploadingVideo && (
                          <button type="button" disabled className="btn-secondary mt-2 inline-flex items-center gap-2 opacity-50">
                            <Upload className="w-4 h-4" /> Enviando...
                          </button>
                        )}
                      </div>
                    ) : (
                      <input
                        type="url"
                        value={formData.hero.video.url}
                        onChange={(e) => setFormData((prev) => ({ ...prev, hero: { ...prev.hero, video: { source: 'link', url: e.target.value } } }))}
                        placeholder="https://www.youtube.com/embed/..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                      />
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-3">
                <button type="button" onClick={() => navigate('/admin/landing-pages')} className="btn-secondary">
                  {t('common.cancel')}
                </button>
                <button type="button" disabled={loading} onClick={(e) => handleSubmit(e as any, false)} className="btn-outline">
                  Salvar rascunho
                </button>
                <button type="button" disabled={loading} onClick={(e) => handleSubmit(e as any, true)} className="btn-primary flex items-center space-x-2">
                  <Save className="w-4 h-4" />
                  <span>{loading ? t('common.saving') : 'Salvar e publicar'}</span>
                </button>
              </div>
            </form>
          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Avatar (destino do CTA)</h3>
              <select
                value={formData.avatar}
                onChange={(e) => setFormData((prev) => ({ ...prev, avatar: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Selecione um avatar</option>
                {avatars.map((avatar) => (
                  <option key={avatar._id} value={avatar._id}>{avatar.name}</option>
                ))}
              </select>
              {avatars.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Nenhum avatar cadastrado ainda. Crie um em <button type="button" className="underline" onClick={() => navigate('/admin/avatars/new')}>Avatares</button>.
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Consultor(a) exibido(a) na página (opcional)</h3>
              <select
                value={formData.teamMember}
                onChange={(e) => setFormData((prev) => ({ ...prev, teamMember: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Nenhum</option>
                {teamMembers.map((member) => (
                  <option key={member._id} value={member._id}>
                    {member.nome}{member.cargo ? ` — ${member.cargo}` : ''}
                  </option>
                ))}
              </select>
              {teamMembers.length === 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Nenhum membro de equipe cadastrado ainda. Crie um em{' '}
                  <button type="button" className="underline" onClick={() => navigate('/admin/equipe')}>Equipe</button>.
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Status</h3>
              <select
                value={formData.status}
                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as 'draft' | 'published' }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="draft">Rascunho</option>
                <option value="published">Publicada</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageForm;
